import { createElement, ReactNode } from 'react';

import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const renderHtmlContent = (htmlString: string): ReactNode[] => {
  const tempElement = document.createElement('div');
  tempElement.innerHTML = htmlString;

  const renderNode = (node: ChildNode): ReactNode => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      const { tagName } = element;

      const styles: Record<string, React.CSSProperties> = {
        li: { marginBottom: '10px', marginLeft: '10px', listStyle: 'disc' },
        p: { marginBottom: '5px' },
        ol: { paddingLeft: '12px' },
        ul: { paddingLeft: '12px' },
      };

      return createElement(
        tagName.toLowerCase(),
        {
          key: Math.random().toString(36).substr(2, 9),
          style: styles[tagName.toLowerCase()] || undefined,
        },
        Array.from(element.childNodes).map(renderNode)
      );
    }

    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      const parts = text.split(/(\#[\w-]+)/); // Split text into parts by hashtags
      return parts.map((part, index) => {
        if (part.startsWith('#')) {
          return createElement(
            'span',
            {
              key: `hashtag-${index}`,
              style: { color: 'var(--link-blue)' },
            },
            part
          );
        }
        return part; // Return plain text part as is
      });
    }

    return null;
  };

  return Array.from(tempElement.childNodes).map(renderNode);
};

export const renderTextContent = (text: string): ReactNode[] => {
  // Split text into paragraphs
  const paragraphs = text.split(/\n\s*\n/);

  return paragraphs.map((paragraph, pIndex) => {
    // Check if paragraph is a list item
    if (
      paragraph.trim().startsWith('- ') ||
      paragraph.trim().startsWith('* ')
    ) {
      return createElement(
        'li',
        {
          key: `list-item-${pIndex}`,
          style: {
            marginBottom: '10px',
            marginLeft: '10px',
            listStyle: 'disc',
          },
        },
        renderTextWithHashtags(paragraph.trim().substring(2))
      );
    }

    // Regular paragraph
    return createElement(
      'p',
      {
        key: `paragraph-${pIndex}`,
        style: { marginBottom: '5px' },
      },
      renderTextWithHashtags(paragraph)
    );
  });
};

const renderTextWithHashtags = (text: string): ReactNode[] => {
  const parts = text.split(/(\#[\w-]+)/);
  return parts.map((part, index) => {
    if (part.startsWith('#')) {
      return createElement(
        'span',
        {
          key: `hashtag-${index}`,
          style: { color: 'var(--link-blue)' },
        },
        part
      );
    }
    return part;
  });
};
